import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { saveOncoObject } from '../../../utils/getOncoObject';

export default function OncoEMRCredentialsModal({
  isOpen,
  onClose,
  oncoData,
  onSuccessfulSubmission,
  accessToken
}) {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    location: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userAccessToken, setUserAccessToken] = useState(null);
  useEffect(() => {
    // If no accessToken is provided, retrieve it
    if (!accessToken) {
      (async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/dashboard/getaccesstoken`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                token: localStorage.getItem("token"),
              },
            }
          );

          if (!response.ok) {
            console.log("Can't get access token " + response);
            alert(
              "Unable to verify credentials to get access token. Please try again or contact Knowtex support to resolve this"
            );
            throw new Error("Network response was not ok");
          }

          const data = await response.json();

          if (data.accessToken) {
            //console.log("Access token " + data.accessToken);
            setUserAccessToken(data.accessToken);
          } else {
            console.error("Access token not available.");
            return null;
          }
        } catch (error) {
          console.error("Failed to fetch access token:", error);
          return null;
        }
      })()
    }
  }, [userAccessToken]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validation logic with a mock sandbox token -- not using any production token 
      const integuruResponse = await fetch(`https://sandbox.integuru.ai/oncoemr/credentials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "INTEGURU-TOKEN": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiMTVlZmQ3MjktYTdlMS00MjgyLWI1YTYtYzVhMmM1ZjFjNjE1IiwiZXhwIjozNTUzOTQwMTczfQ.68OGo6qavRNUMJ-Z-qB6IDJut3JOkTf3jhjHHJ2jjD0",
        },
        body: JSON.stringify({
          email: credentials.username,
          password: credentials.password,
          domain: oncoData.secureUrl,
        })
      });

      if (!integuruResponse.ok) {
        setIsSubmitting(false);
        toast.error('Invalid OncoEMR credentials. Please check your OncoEMR username and password.');
        return;
      }

      toast.success('OncoEMR credentials validated successfully!');
      const finalCredentials = {
        username: credentials.username,
        password: credentials.password,
        domain: oncoData.secureUrl,
        location: credentials.location
      };
      // Save the credentials using the utility function
      await saveOncoObject(finalCredentials);

      if ((oncoData.superOrg === "TenOnc" || oncoData.superOrg === "Ironwood") && (accessToken || userAccessToken)) {
        const integuruResponse = await fetch(`https://production.integuru.ai/oncoemr/credentials`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "INTEGURU-TOKEN": accessToken ? accessToken : userAccessToken,
          },
          body: JSON.stringify({
            email: credentials.username,
            password: credentials.password,
            domain: oncoData.secureUrl,
            location: credentials.location
          })
        });

        if (!integuruResponse.ok) {
          setIsSubmitting(false);
          toast.error('Invalid Onco EMR credentials. Please check your username and password.');
          return;
        }
        toast.success('Onco EMR credentials bound successfully!');
      }
      // Call the onSuccessfulSubmission callback to close the modal and refresh data
      if (onSuccessfulSubmission) {
        onSuccessfulSubmission();
      }

      onClose();
    } catch (error) {
      toast.error('Failed to validate credentials. Please try again.');
      console.error('Credential validation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCredentials({ username: '', password: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Connect to OncoEMR</h2>
              <p className="text-sm text-gray-500">{oncoData.superOrg} Integration</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Info Section */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-blue-900 mb-1">Secure Connection Required</h3>
                <p className="text-sm text-blue-700">
                  Please enter your OncoEMR credentials to enable seamless patient data integration.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                OncoEMR Location <span className="text-gray-400">(Optional)</span>
              </label>
              <select
                id="location"
                name="location"
                value={credentials.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                disabled={isSubmitting}
              >
                <option value="">Select your OncoEMR location</option>
                {oncoData.locations.map((location, index) => (
                  <option key={index} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                OncoEMR Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={credentials.username}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your OncoEMR username"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                OncoEMR Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your OncoEMR password"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Security Notice */}
            <div className="bg-gray-50 rounded-md p-3">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-xs text-gray-600">
                  Your credentials are protected with industry-standard encryption
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={isSubmitting || !credentials.username || !credentials.password}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Connecting...</span>
                  </div>
                ) : (
                  'Connect to OncoEMR'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};